import _ from "lodash";
import React, { useId, useState, useEffect } from "react";
import { connect } from "react-redux";

import {
  Modal,
  Button,
  ToggleSwitch,
  TextInput,
  IconButton,
  FormInputError,
} from "@toddle-edu/ds-web";
import {
  CloseOutlined,
  PencilLineOutlined,
  TickLargeOutlined,
} from "@toddle-edu/ds-icons";

import { useI18n } from "Hooks";
import classNames from "classnames";
import { compose, graphql } from "lib/apolloClient";
import { FormTextInput } from "UIComponents/InputTextField";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import Loading from "UIComponents/Loading";

import { GRAPHQL_ONGOING_QUERY_NETWORK_STATUS } from "Constants";
import { getInternalToolGenericDataVariables } from "AddOnManager/modules/AddOnManagerUtils";
import {
  IBIS_REGISTRATION_REVAMP,
  ORGANIZATION,
} from "AddOnManager/routes/OrgAddOn/modules/OrgAddOnContants";
import { updateAddOnStatus } from "AddOnManager/routes/OrgAddOn/modules/OrgAddOnHelpers";
import { getInternalToolGenericDataQuery } from "Tooling/routes/InternalTools/modules/InternalToolsQuery";

import classes from "./IBISRevamp.scss";
import { getIsIBISEnabledInAddOnManager } from "Utils/ACLStoreUtils";

const IBISRevamp = ({
  orgId,
  isLoading,
  isIBISRevampEnabled,
  schoolCode,
  queryVariables,
  updateAddOnStatus,
  closeModal,
  onSuccessAddOnConfigChange = null,
}) => {
  const { t } = useI18n();
  const schoolCodeErrorId = useId();
  const [isAddOnChecked, setIsAddOnChecked] = useState(
    isIBISRevampEnabled || false
  );
  const [schoolCodeInput, setSchoolCodeInput] = useState(schoolCode || "");
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSchoolCodeValid, setIsSchoolCodeValid] = useState(true);
  const [fullScreenLoader, setFullScreenLoader] = useState(false);

  const localTestId = "addOnManager-ibisRevamp";

  const canEditSchoolCode = getIsIBISEnabledInAddOnManager();

  // to display the latest values fetched from query
  useEffect(() => {
    setIsAddOnChecked(isIBISRevampEnabled || false);
  }, [isIBISRevampEnabled]);

  useEffect(() => {
    setSchoolCodeInput(schoolCode || "");
  }, [schoolCode]);

  const handleToggleEditMode = value => () => {
    setIsEditMode(value);
    if (!value) {
      setSchoolCodeInput(schoolCode || "");
      setIsSchoolCodeValid(true);
    }
  };

  const handleTextInputChange = e => {
    setSchoolCodeInput(e?.target?.value);
    setIsSchoolCodeValid(prevIsSchoolCodeValid => {
      if (!prevIsSchoolCodeValid) {
        return true;
      }
      return prevIsSchoolCodeValid;
    });
  };

  const handleTextInputSave = async () => {
    // 6 digit numeric validation if school code is entered
    const isValid =
      _.isEmpty(schoolCodeInput) || /^\d{6}$/.test(schoolCodeInput);
    setIsSchoolCodeValid(isValid);

    if (!isValid) {
      return;
    }

    setFullScreenLoader(true);

    await updateAddOnStatus({
      orgId,
      queryVariables,
      addOnType: IBIS_REGISTRATION_REVAMP,
      updateOn: ORGANIZATION,
      additionalInput: { schoolCode: schoolCodeInput },
    });
    if (!_.isNull(onSuccessAddOnConfigChange)) onSuccessAddOnConfigChange();
    setIsEditMode(false);
    setFullScreenLoader(false);
  };

  const handleToggleChange = async () => {
    setFullScreenLoader(true);
    setIsAddOnChecked(prevIsAddOnChecked => !prevIsAddOnChecked);

    await updateAddOnStatus({
      orgId,
      queryVariables,
      addOnType: IBIS_REGISTRATION_REVAMP,
      updateOn: ORGANIZATION,
      newStatus: !isAddOnChecked,
    });
    if (!_.isNull(onSuccessAddOnConfigChange)) onSuccessAddOnConfigChange();
    setFullScreenLoader(false);
  };

  return (
    <Modal
      width="580px"
      height="700px"
      dsVersion="2.0"
      onClose={closeModal}
      isOpen
    >
      <Modal.Header
        title={t("common:enable_with_label", {
          label: t("common:ibis_registration_revamp"),
        })}
        isClosable
      />
      <Modal.Body isScrollable>
        {!isLoading ? (
          <div className={classes.container}>
            <FormTextInput
              size="large"
              label={t("common:org_id")}
              error=""
              value={orgId}
              isRequired={true}
              disabled={true}
            />

            <div className={classes.switchContainer}>
              <div>{t("common:ibis_registration_revamp")}</div>
              <div className={classes.actionItems}>
                <ToggleSwitch
                  dsVersion="2.0"
                  checked={isAddOnChecked}
                  onChange={handleToggleChange}
                  testId={`${localTestId}-toggleSwitch`}
                />
              </div>
            </div>

            <div className={classes.textContainer}>
              <div className={classes.labelContainer}>
                {t("common:ib_school_code")}
              </div>
              <div className={classes.inputContainer}>
                <div>
                  {isEditMode ? (
                    <div className={classes.textInputContainer}>
                      <TextInput
                        autoFocus
                        dsVersion="2.0"
                        value={schoolCodeInput}
                        onChange={handleTextInputChange}
                        error={isSchoolCodeValid ? "" : "error"}
                        testId={`${localTestId}-schoolCodeInput`}
                        aria-describedby={schoolCodeErrorId}
                      />
                      <FormInputError
                        id={schoolCodeErrorId}
                        dsVersion="2.0"
                        {...(!isSchoolCodeValid && {
                          error: t("common:ib_school_code_input_error"),
                          className: classes.errorContainer,
                        })}
                      />
                    </div>
                  ) : (
                    <div
                      className={classNames(
                        classes.valueContainer,
                        !schoolCodeInput ? "text-textSubtle" : "text-label-2"
                      )}
                    >
                      {schoolCodeInput || <i>{t("common:no_value_added")}</i>}
                    </div>
                  )}
                </div>
                {canEditSchoolCode ? (
                  <div className={classes.buttonContainer}>
                    {isEditMode ? (
                      <>
                        <IconButton
                          dsVersion="2.0"
                          icon={<CloseOutlined />}
                          type="outlined"
                          variant="neutral"
                          onClick={handleToggleEditMode(false)}
                          testId={`${localTestId}-cancelEdit`}
                        />
                        <IconButton
                          dsVersion="2.0"
                          variant={"progressive"}
                          icon={<TickLargeOutlined />}
                          onClick={handleTextInputSave}
                          testId={`${localTestId}-saveSchoolCode`}
                        />
                      </>
                    ) : (
                      <IconButton
                        dsVersion="2.0"
                        icon={<PencilLineOutlined />}
                        type="plain"
                        variant="neutral"
                        onClick={handleToggleEditMode(true)}
                        testId={`${localTestId}-editSchoolCode`}
                      />
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : (
          <Loading />
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className={classes.footer}>
          <div className={classes.rightFooterContainer}>
            <Button
              variant="outlined-subtle"
              size="large"
              dsVersion="2.0"
              onClick={closeModal}
            >
              {t("common:close")}
            </Button>
          </div>
        </div>
      </Modal.Footer>

      {fullScreenLoader ? <FullScreenLoader /> : null}
    </Modal>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { orgId } = ownProps;

  const queryVariables = getInternalToolGenericDataVariables({
    orgId,
    addOnType: IBIS_REGISTRATION_REVAMP,
  });

  return {
    isData: true,
    isLoading: false,
    queryVariables,
  };
};

const mapActionCreators = { updateAddOnStatus };

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getInternalToolGenericDataQuery, {
    name: "getInternalToolGenericData",
    skip: ({ orgId }) => _.isEmpty(orgId),
    options: ({ queryVariables }) => ({
      variables: queryVariables,
      fetchPolicy: "cache-and-network",
    }),
    props: ({
      getInternalToolGenericData,
      ownProps: { isData, isLoading },
    }) => {
      const data = _.get(
        getInternalToolGenericData,
        "platform.internalToolsGenericQuery.data",
        {}
      );

      const { schoolCode, permissionValue: isIBISRevampEnabled } = data;

      return {
        isData,
        isIBISRevampEnabled,
        schoolCode,
        isLoading:
          _.includes(
            GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
            _.get(getInternalToolGenericData, "networkStatus")
          ) || isLoading,
      };
    },
  })
)(IBISRevamp);
